/*
 * squary.js: storefront tracking for Squary (zones, clicks, scroll, frustration).
 * Loaded by snippets/squary.liquid. Runs only with analytics consent
 * (Shopify.customerPrivacy.analyticsProcessingAllowed(), which Cookie Confirm syncs).
 *
 * Zones, innermost first: data-sq-zone="<name>", data-placement (mosy banners), else the
 * Shopify section (zone = section id without the template prefix; sql zone_labels names them),
 * else an app (cookie banner, chat) or a theme component outside sections (sticky add to cart).
 * Component (clicks only): the nearest data-component of the theme plus the action, e.g.
 * "productItem:wishlist", "productForm:add_to_cart", "cartDrawer:checkout".
 * Events are queued and sent with sendBeacon when the page is hidden, or when 50 are
 * queued, so a page view costs ~1–2 requests.
 *
 * Shop events: a successful /cart/add (fetch, XHR or form) is 'add_to_cart' (target = product id,
 * placement = variant id, ms = quantity); heading to the checkout is 'begin_checkout'. After the
 * first add to cart the cart gets the attribute _sq_sid (the Squary session), so orders can be
 * linked back to sessions and zones. Clarity recordings are tagged with the same sq_sid.
 *
 * Replay: cfg.replay (0..1) of sessions are recorded with rrweb (served by the collector at
 * /rrweb.js): form fields always masked, account pages never recorded, chunks of a few seconds
 * sent to <collector>/r.
 *
 * Also (v4): the visit's channel on every page_view (placement; target = referrer host), JS errors
 * and failed cart calls (js_error), and Core Web Vitals per page view (vital).
 * v5: clicks carry the zone's size in px (zw, zh), so heatmaps line up at every screen width.
 * v6: clicks carry their position inside the clicked component (cx, cy), so they land on that
 * component on any page of the same type (product pages have different layouts).
 * v8: session replay also from the first add to cart (on top of the 20% sample), pieces every 20 s.
 * v7: experiments (snippets/squary-experiment.liquid, window.SquaryExp): every batch carries the visitor's
 * variants as x ('pendant-ai:B,other:A'), and each page view of a running test's page sends 'exp_view'
 * (target = experiment key, placement = variant), so exposure is known, not just assignment.
 * v9: product-card impressions on collection and search grids. A card at least half visible for 1 s
 * (uninterrupted) is recorded once per page view and grid context; they are sent as one 'impressions'
 * event per context per flush (at most 60 cards each): grid = 'source|page|per_page|filtered|sort'
 * (source grid / ai / search, per_page 0 on the last page, filtered 0/1, sort = sort_by or ''), items =
 * 'index:productId,...' (index = 1-based place among the product cards of that grid on this page).
 * Clicks inside such a card carry the same grid and items = 'index:productId'.
 * v9.1: script-made clicks (isTrusted false) are not recorded: they showed up as rage clicks on AI filter results.
 * v10: clicks (and rage / dead clicks) say which element was clicked and where inside it, so the heatmap puts the dot on
 * that element on the capture at any screen width and in any language: ek = 'tag.firstClass~n' of the clicked element
 * (the nearest link, else button / input / label / ..., as for target; n = its place among the elements of the same tag
 * and class in its scope: the product card of a collection / search grid, else the zone), ex, ey = the click's position
 * inside that element (0..1). Squary's capture (backend analytics/screenshots.py ELEMENTS_JS) names elements the same way.
 * Also v10: the click the browser adds on a label's input (a swatch or checkbox click arrived twice, label then input, and
 * two quick swatch clicks counted as a rage click) is no longer recorded.
 * v11: a buyer's replay shows the whole page that led to the add to cart, not only what came after it. Sessions outside the
 * cfg.replay sample record the current page view with rrweb into memory only (the 'page buffer'), on the page types of
 * cfg.replay_buffer_pages (default 'product'; 'all' or template names like 'product,collection'; cfg.replay_buffer: 0 turns it
 * off). Nothing is sent unless an add to cart happens on that page: then the buffer is sent as this page view's replay (the
 * same /r chunks, seq 0, 1, ..., so the collector, backend and player are unchanged) and recording carries on as in v8.
 * Leaving the page without an add to cart drops the buffer, unsent. Only the current page is buffered: earlier pages of the
 * session are never kept, so a buyer's replay starts at the page of the add to cart (or at the start of the session in the
 * sample). The buffer is capped (cfg.replay_buffer_s, default 300 s, and cfg.replay_buffer_kb, default 2500 KB of incremental
 * events): at half the cap rrweb takes a fresh full snapshot (a checkout) and the oldest of two segments is dropped, so a cut
 * buffer still replays. Same masking (all inputs masked, sq-mask / sq-block), same sampling, same pages never recorded, and
 * only with analytics consent, as before. Events sent to /e are unchanged.
 * v12: theme events (document 'squary:ui': the hero's swipes and autoplay), sent as exp_view 'ui:<name>'. And a zone counts
 * as seen only when visible across at least half its width too, and a click in a zone counts it as seen. A checkout
 * link counts as a checkout start by where it goes (the cart page's relative href="checkout" was missed): a slide just off screen (the hero's
 * 2nd banner on mobile) touches the screen's edge with 0 px width and its full height, and counted as seen (23 Oct 2026).
 * v13: an unhandled promise rejection gets its file and line from the error's stack when the browser has one (only Firefox
 * gives fileName): rejections came without a source, so ours and other sites' scripts couldn't be told apart (24 Oct 2026).
 * v14: a page loaded in a background tab paints only when it's shown, so its LCP and FCP measured the wait, not the
 * loading (a product opened in a new tab and looked at 57 s later reported 57 s: 62% of the "poor" product page LCPs,
 * mostly desktop Chrome). As Google's web-vitals: paints after the page was first hidden are not sent, and a
 * prerendered page counts from its activation (28 Oct 2026).
 */
(function () {
  'use strict';
  var cfg = window.Squary || {};
  if (!cfg.endpoint || window.__squary) return;
  window.__squary = true;

  // --- v12: theme events (swipes, autoplay, ...) -------------------------------------------------------------------
  // Theme code dispatches document 'squary:ui' events, detail {name, how, zone, to}: the hero's slide changes
  // (snippets/squary-hero-auto.liquid: how = swipe | dot | auto). Kept in memory like errors, sent by start() (only with
  // consent) as exp_view with target 'ui:<name>' (the collector has a fixed list of event types; every reader of exp_view
  // matches a test key, which never has a colon), placement = how, zone, component = 'to:<n>', ms = since the page loaded.
  var uiSink = [];
  document.addEventListener('squary:ui', function (ev) {
    var d = ev.detail || {};
    if (!/^[a-z0-9-]{1,30}$/.test(String(d.name || ''))) return;
    var e = { target: 'ui:' + d.name, placement: String(d.how || '').slice(0, 20) || null, zone: d.zone ? String(d.zone).slice(0, 60) : null,
              component: d.to != null ? 'to:' + String(d.to).slice(0, 20) : null, ms: Math.round(performance.now()) };
    if (typeof uiSink === 'function') uiSink(e); else if (uiSink.length < 100) uiSink.push(e);
  });

  // --- Errors: caught from the first moment, kept in memory, sent by start() (only with consent) --
  // js_error: target = message (emails and long numbers masked, so equal errors group), component =
  // file:line, placement = kind (error, promise, resource, cart_api), ms = HTTP status for cart_api.
  var errorSink = [], errorKeys = {}, errorCount = 0;
  function reportError(kind, msg, src, line, status) {
    msg = String(msg || '').replace(/\s+/g, ' ').replace(/[^\s@"'<>]+@[^\s@"'<>]+/g, '<email>').replace(/\d{5,}/g, '<n>').slice(0, 300);
    var where = '';
    try {
      if (src) {
        var u = new URL(src, location.href);
        var file = u.pathname.split('/').pop() || u.pathname;
        where = (u.host === location.host || /cdn\/shop|cdn\.shopify\.com/.test(u.host + u.pathname) ? file : u.host + '/' + file) + (line ? ':' + line : '');
      }
    } catch (e) {}
    var key = kind + '|' + msg + '|' + where;
    if (!msg || errorKeys[key] || errorCount >= 10) return;  // each error once, at most 10 per page view
    errorKeys[key] = true;
    errorCount++;
    var ev = { target: msg, component: where.slice(0, 120) || null, placement: kind, ms: status || null };
    if (typeof errorSink === 'function') errorSink(ev); else errorSink.push(ev);
  }
  window.addEventListener('error', function (ev) {
    var el = ev.target;
    if (el && el !== window && el.tagName) {  // a script, stylesheet or image that failed to load
      var url = el.src || el.href || '';
      // only the store's own files: blocked ad and tracking pixels are not our problem
      if (/^(SCRIPT|LINK|IMG)$/.test(el.tagName) && /^https?:\/\/([^/]*lamptwist\.com|cdn\.shopify\.com)\//.test(url)) {
        reportError('resource', el.tagName.toLowerCase() + ' failed to load', url);
      }
      return;
    }
    reportError('error', ev.message || (ev.error && ev.error.message), ev.filename, ev.lineno);
  }, true);
  // v13: the first frame with a URL in a stack: Safari / Firefox 'fn@https://…/x.js:12:34', Chrome 'at fn (https://…/x.js:12:34)'
  function stackFrame(r) {
    var m = r && typeof r.stack === 'string' && r.stack.match(/(https?:\/\/[^\s()@]+?):(\d+)(?::\d+)?(?=[\s)]|$)/m);
    return m ? [m[1], +m[2]] : [];
  }
  window.addEventListener('unhandledrejection', function (ev) {
    var r = ev.reason, at = r && r.fileName ? [r.fileName, r.lineNumber] : stackFrame(r);
    reportError('promise', (r && (r.message || r.toString && r.toString())) || 'unhandled rejection', at[0], at[1]);
  });

  var ZONE = '[data-sq-zone],[data-placement]';
  var SECTION = '.shopify-section,[id^="shopify-section-"]';
  var APPS = [['#cc-consent-banner', 'app/cookie-banner'], ['#gorgias-chat-container', 'app/gorgias-chat'],
              ['[data-sq-c="productStickyAtc"],[data-component="productStickyAtc"]', 'productStickyAtc']];
  // Actions inside a component, most specific first.
  var ACTIONS = [
    ['[data-checkout-button]', 'checkout'], ['[data-submit-button]', 'add_to_cart'],
    ['shopify-accelerated-checkout,shopify-accelerated-checkout-cart,.shopify-payment-button', 'express_checkout'],
    ['[data-wishlist-button]', 'wishlist'], ['[data-open-popup-button]', 'quick_view'],
    ['[data-select-variant-and-toggle]', 'variant'], ['[data-back],[data-next],[data-slider-button]', 'arrows'],
    ['[data-desktop-sort],[data-sort-wrapper]', 'sort'], ['[data-clear-all],[data-all-filters-reset],[data-filter-reset]', 'filter_reset'],
    ['[data-toggle-filter],[data-toggle-filter-popup]', 'filter_toggle'], ['[data-go-to-search]', 'search_all'],
    ['[data-predictive-product-results]', 'suggestion'], ['[data-load-more-button]', 'load_more'],
    ['nav[aria-label=pagination]', 'pagination'], ['[data-logo]', 'logo'], ['[data-cart-button]', 'cart_icon'],
    ['[data-desktop-menu-button],[data-menu-button],[data-sub-menu-button],[data-sub-menu-select-button]', 'menu_button'],
    ['[data-desktop-menu-dropdown-content],[data-sub-menu],[data-navigation]', 'menu_link'],
    ['[data-toggle-info],[data-toggle-explanation],[data-explanation-modal]', 'info'],
    ['[data-product-usp],[data-shipping-usp]', 'usp'], ['[data-line-items]', 'cart_line'],
    ['#shopify-section-header [data-input],#shopify-section-header input', 'search_box'],
    ['[data-option],[data-input-wrapper]', 'option'], ['[data-link]', 'product_link']
  ];
  var SESSION_MIN = 30;
  var FLUSH_AT = 50;
  var VIEW_MS = 1000;   // a zone counts as seen after 1 s at least half visible
  var BASE = cfg.endpoint.replace(/\/e$/, '');
  var REPLAY_RATE = typeof cfg.replay === 'number' ? cfg.replay : 0.2;
  var NO_REPLAY = /^\/(account|challenge|password|checkouts?|orders)(\/|$)/;
  // v11: page buffer (window.Squary: replay_buffer 0/1, replay_buffer_pages 'all' or template names like 'product,collection',
  // replay_buffer_s, replay_buffer_kb): the cap of the buffer, in seconds and KB of incremental events.
  var BUFFER_PAGES = String(cfg.replay_buffer_pages || 'product').split(',').map(function (x) { return x.trim(); });
  var BUFFER_ON = cfg.replay_buffer !== 0 && cfg.replay_buffer !== false &&
    (BUFFER_PAGES.indexOf('all') >= 0 || BUFFER_PAGES.indexOf(String(cfg.template || '').split('.')[0]) >= 0);
  var BUFFER_MS = (cfg.replay_buffer_s > 0 ? cfg.replay_buffer_s : 300) * 1000;
  var BUFFER_BYTES = (cfg.replay_buffer_kb > 0 ? cfg.replay_buffer_kb : 2500) * 1024;
  var HOVER_MS = 300;   // shorter hovers are just the mouse passing by
  // v9: product cards in the collection / search grid (the theme's mount renames data-component to data-sq-c)
  var CARD = '[data-sq-c="productItem"],[data-component="productItem"]';
  var GRID = '[data-product-grid]';
  var AI_GRID = '[data-sq-zone="ai-results"]';
  var GRID_PAGE = /^(collection|search)(\.|$)/.test(cfg.template || '');
  var IMP_MAX = 60;     // cards per 'impressions' event (keeps the row well under the collector's limits)

  function consented() {
    try { return !!(window.Shopify && Shopify.customerPrivacy && Shopify.customerPrivacy.analyticsProcessingAllowed()); }
    catch (e) { return false; }
  }

  function idle(fn) {
    return window.requestIdleCallback ? requestIdleCallback(fn, { timeout: 2000 }) : setTimeout(fn, 200);
  }

  function id() {
    return (window.crypto && crypto.randomUUID) ? crypto.randomUUID().replace(/-/g, '')
      : (Date.now().toString(36) + Math.random().toString(36).slice(2, 12));
  }

  function cookie(name) {
    var m = document.cookie.match('(?:^|; )' + name + '=([^;]*)');
    return m ? decodeURIComponent(m[1]) : null;
  }

  function setCookie(name, value) {
    document.cookie = name + '=' + encodeURIComponent(value) + '; path=/; max-age=' + SESSION_MIN * 60 + '; SameSite=Lax; Secure';
  }

  // Squary session: 30 min rolling, first-party cookie (with the session's channel next to it).
  function sessionId() {
    var sid = cookie('_sq_sid') || id();
    setCookie('_sq_sid', sid);
    var ch = cookie('_sq_ch');
    if (ch) setCookie('_sq_ch', ch);
    return sid;
  }

  // Where this visit came from, close to GA4's default channel groups (UTM tags, ad click ids,
  // else the referrer). Sent on every page_view (placement), so any screen can split by it.
  function channelOf() {
    var p = new URLSearchParams(location.search);
    var m = (p.get('utm_medium') || '').toLowerCase(), s = (p.get('utm_source') || '').toLowerCase();
    var mk = m.replace(/[\s_+-]+/g, '');  // 'paid social', 'paid_social', 'paid-social' -> 'paidsocial'
    // AI assistants: by utm_source (ChatGPT adds utm_source=chatgpt.com) or by referrer below
    var ai = /chatgpt|openai|perplexity|gemini|bard|copilot|claude|anthropic|mistral|deepseek|grok|meta\.ai|you\.com|phind|poe\.com/;
    var ref = '';
    try { ref = document.referrer ? new URL(document.referrer).hostname.replace(/^www\./, '') : ''; } catch (e) {}
    // our own pages, and coming back from checkout or a payment page, are not a new source
    if (ref === location.hostname.replace(/^www\./, '') || /lamptwist|shopify|shop\.app|paypal|adyen|klarna|mollie|bancontact|stripe/.test(ref)) ref = '';
    var social = /facebook|instagram|^fb$|^ig$|meta|pinterest|tiktok|snapchat|linkedin|twitter|^x$|^t\.co$|youtube|reddit/;
    if (p.get('gclid') || p.get('gbraid') || p.get('wbraid') || p.get('gad_source') || p.get('msclkid') ||
        /^(cpc|ppc|paid|paidsearch|shopping|display|cpm|cpv|paidsocial)$/.test(mk)) {
      if (social.test(s) || /social/.test(m)) return 'Paid Social';
      if (/bing|microsoft/.test(s) || p.get('msclkid')) return 'Microsoft Ads';
      if (!s || /google|youtube/.test(s)) return 'Google Ads';
      return 'Paid Other';
    }
    if (ai.test(s)) return 'AI Assistant';
    if (/e-?mail|newsletter|klaviyo/.test(m + ' ' + s)) return 'Email';
    if (/affiliate/.test(m)) return 'Affiliate';
    if (/social/.test(m) || (m && social.test(s))) return 'Organic Social';
    if (ref) {
      if (ai.test(ref)) return 'AI Assistant';
      if (/(^|\.)mail\.|outlook|webmail/.test(ref)) return 'Email';
      if (/(^|\.)(google|bing|duckduckgo|yahoo|ecosia|qwant|yandex|baidu|startpage|brave)\./.test(ref)) return 'Organic Search';
      if (social.test(ref) || /(^|\.)(facebook|instagram|pinterest|linkedin|tiktok|youtube|reddit)\.|^t\.co$/.test(ref)) return 'Organic Social';
      return 'Referral';
    }
    if (p.get('fbclid')) return 'Organic Social';
    if (s) return 'Other campaign';
    return 'Direct';
  }

  // GA client id from _ga (GA1.1.123.456 -> 123.456), to link to GA4 later.
  function gaClientId() {
    var ga = cookie('_ga');
    return ga ? ga.split('.').slice(-2).join('.') : null;
  }

  function sectionName(s) {
    return s.id.replace(/^shopify-section-/, '').replace(/^(template|sections)--\d+__/, '');
  }

  function zoneOf(el) {
    if (!el || !el.closest) return null;
    var z = el.closest(ZONE);
    if (z) return { el: z, zone: z.getAttribute('data-sq-zone') || ('placement/' + z.getAttribute('data-placement')),
                    placement: z.getAttribute('data-placement') };
    for (var i = 0; i < APPS.length; i++) {
      var a = el.closest(APPS[i][0]);
      if (a) return { el: a, zone: APPS[i][1], placement: null };
    }
    var s = el.closest(SECTION);
    return s && s.id ? { el: s, zone: sectionName(s), placement: null } : null;
  }

  // Component of the clicked element; actions only count inside that component (or the zone).
  function componentOf(el, zoneEl) {
    var c = el.closest('[data-sq-c],[data-component]');
    if (c === document.body || (c && zoneEl && !zoneEl.contains(c))) c = null;
    var name = c ? (c.getAttribute('data-sq-c') || c.getAttribute('data-component')) : '';
    var scope = c || zoneEl;
    for (var i = 0; i < ACTIONS.length; i++) {
      var a = el.closest(ACTIONS[i][0]);
      if (a && (!scope || scope.contains(a))) return (name ? name + ':' : '') + ACTIONS[i][1];
    }
    var cc = el.closest('[id^="cc-"]');  // cookie banner buttons: cc-accept-all, cc-reject-all, ...
    if (cc) return 'cookieBanner:' + cc.id.slice(3);
    return name || null;
  }

  // Path without the language prefix (/nl/collections/umage -> /collections/umage).
  var LANG = /^\/(nl|fr|de|es|pt|pl|it|da|en|sv|fi|no|cs)(?=\/|$)/;
  // Account reset/activation links carry a personal token: keep only the prefix.
  function bare(path) {
    return (path.replace(LANG, '') || '/').replace(/^(\/account\/(reset|activate|invoices|orders))\/.+$/, '$1/…');
  }

  // Clicked link (path, so tiles are keyed by their link, not their position) or element.
  function targetOf(el) {
    var a = el.closest && el.closest('a[href]');
    if (a) {
      try {
        var u = new URL(a.href);
        if (!/^https?:$/.test(u.protocol)) return u.protocol.replace(':', '');  // mailto:, tel: … never the address
        return u.host === location.host ? bare(u.pathname) : u.host + u.pathname;
      } catch (e) {}
    }
    var b = el.closest && el.closest('a,button,[role=button],input,select,label,summary') || el;
    var cls = (typeof b.className === 'string' && b.className.trim().split(/\s+/)[0]) || '';
    return b.tagName.toLowerCase() + (b.id ? '#' + b.id : cls ? '.' + cls : '');
  }

  // v10: the clicked element (the nearest link, else button / input / label / ..., as for target) named
  // 'tag.firstClass~n': n = its 1-based place among the elements of the same tag and class in its scope, the product
  // card of a grid (cards repeat the same elements) or else the zone. Classes, unlike ids and texts, are the same in
  // every language. Kept in step with ELEMENTS_JS in the backend's analytics/screenshots.py.
  var HIT = 'a,button,[role=button],input,select,label,summary';
  function hitOf(el) { return el.closest('a[href]') || el.closest(HIT); }
  function gridCardOf(el) { var c = el.closest(CARD); return c && c.closest(GRID) ? c : null; }
  function kindOf(h) {
    var cls = (typeof h.className === 'string' && h.className.trim().split(/\s+/)[0]) || '';
    return h.tagName.toLowerCase() + (cls ? '.' + cls.slice(0, 40) : '');
  }
  function elKey(el, z, ev) {
    var h = el.closest && hitOf(el);
    if (!h || !z || !z.el.contains(h)) return null;
    var k = kindOf(h), card = gridCardOf(h), scope = card || z.el;
    var list = scope.querySelectorAll(HIT), seen = typeof Set === 'function' ? new Set() : null, n = 0;
    for (var i = 0; i < list.length; i++) {
      var c = hitOf(list[i]);
      if (!c || (seen && seen.has(c))) continue;
      if (seen) seen.add(c);
      if (card ? !card.contains(c) : gridCardOf(c)) continue;
      if (kindOf(c) !== k) continue;
      var cz = zoneOf(c);
      if (!cz || cz.el !== z.el) continue;
      n++;
      if (c === h) break;
    }
    if (!n) return null;
    var b = h;
    if ((h.offsetWidth < 1 || h.offsetHeight < 1) && h.tagName === 'INPUT') b = (h.labels && h.labels[0]) || h.closest('label') || h;  // hidden radio: its label
    var r = b.getBoundingClientRect(), out = { key: k + '~' + n };
    if (r.width >= 1 && r.height >= 1) {
      out.x = Math.round(Math.min(1, Math.max(0, (ev.clientX - r.left) / r.width)) * 1000) / 1000;
      out.y = Math.round(Math.min(1, Math.max(0, (ev.clientY - r.top) / r.height)) * 1000) / 1000;
    }
    return out;
  }

  function interactive(el) {
    // any <a>: the theme opens search and drawers from links without href
    return !!(el.closest && el.closest('a,button,input,select,textarea,label,summary,[role=button],[onclick],[tabindex]'));
  }

  // A stable number 0..99 from the session id: the same session is either always or never recorded.
  function bucketOf(id) {
    var h = 0;
    for (var i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) % 1000003;
    return h % 100;
  }

  // Experiments (v7): the variants the head snippet assigned; empty when there is no running test.
  function experiments() {
    try { return window.SquaryExp ? String(window.SquaryExp.str() || '').slice(0, 200) : ''; } catch (e) { return ''; }
  }
  function expViews() {
    try { return (window.SquaryExp && window.SquaryExp.active()) || []; } catch (e) { return []; }
  }

  // v9: the product id of a card (from the theme's product JSON), cached per card element.
  var cardIds = typeof WeakMap === 'function' ? new WeakMap() : null;
  function cardPid(card) {
    var v = cardIds && cardIds.get(card);
    if (v) return v;
    var s = card.querySelector('script[data-product-data-json]');
    var txt = s ? s.textContent : '';
    var m = txt.match(/"product"\s*:\s*\{\s*"id"\s*:\s*"?(\d+)/);
    if (m) v = m[1];
    else { try { v = String(JSON.parse(txt).product.id); } catch (e) { v = card.getAttribute('data-sq-ai-pid') || card.getAttribute('data-product-id'); } }
    v = v && /^\d{1,20}$/.test(v) ? v : null;
    if (v && cardIds) cardIds.set(card, v);
    return v;
  }

  // v9: where a card sits: its grid context and its 1-based index among the product cards of that grid.
  function gridOf(card) {
    if (!GRID_PAGE || !card || !card.closest) return null;
    var grid = card.closest(GRID);
    if (!grid) return null;
    var ai = card.closest(AI_GRID);
    var pid = cardPid(card);
    if (!pid) return null;
    var list = [].filter.call((ai || grid).querySelectorAll(CARD), function (c) { return ai || !c.closest(AI_GRID); });
    var idx = list.indexOf(card) + 1;
    if (idx < 1) return null;
    var p = new URLSearchParams(location.search);
    var src = ai ? 'ai' : /^search/.test(cfg.template) ? 'search' : 'grid';
    var page = ai ? 1 : Math.max(1, Math.min(9999, parseInt(p.get('page'), 10) || 1));
    var per = 0, filtered = 0, sort = '';
    if (!ai) {
      // cards per page: known when a later page exists (the last page can be shorter); 0 = unknown
      var nav = document.querySelector('nav[aria-label=pagination]');
      var later = nav && [].some.call(nav.querySelectorAll('a[href*="page="]'), function (a) {
        var n = /[?&]page=(\d+)/.exec(a.getAttribute('href') || '');
        return n && +n[1] > page;
      });
      if (later) per = Math.min(999, list.length);
      p.forEach(function (val, k) { if (/^filter\./.test(k) && val) filtered = 1; });
      if (/^\/collections\/[^/]+\/[^/]+/.test(bare(location.pathname))) filtered = 1;  // tag path (/collections/x/tag)
      sort = (p.get('sort_by') || '').toLowerCase().replace(/[^a-z0-9-]/g, '').slice(0, 40);
    }
    return { ctx: src + '|' + page + '|' + per + '|' + filtered + '|' + sort, idx: idx, pid: pid };
  }

  function start() {
    var freshSession = !cookie('_sq_sid');
    var sid = sessionId();
    // A new source (campaign, search, other site) sets the channel; a page inside the visit keeps it.
    var ch = channelOf();
    if (!freshSession && ch === 'Direct' && cookie('_sq_ch')) ch = cookie('_sq_ch');
    setCookie('_sq_ch', ch);
    var pvid = id();
    var seq = 0;
    var queue = [];
    var impBuf = {}, impSeen = {};  // v9: cards seen since the last flush, per grid context; every card recorded
    var maxScroll = 0;
    var visibleMs = 0;
    var visibleSince = document.visibilityState === 'visible' ? Date.now() : null;

    function push(type, e) {
      e = e || {};
      e.seq = seq++;
      e.t = Date.now();
      e.type = type;
      queue.push(e);
      if (type === 'add_to_cart' && window.__sqReplayFromCart) { try { window.__sqReplayFromCart(); } catch (x) {} }
      if (queue.length >= FLUSH_AT) flush();
    }

    // v9: the cards seen since the last flush go into the batch as 'impressions' events (one per grid
    // context, at most IMP_MAX cards each). Added straight to the queue: push() could flush again.
    function takeImpressions() {
      for (var ctx in impBuf) {
        var list = impBuf[ctx];
        for (var i = 0; i < list.length; i += IMP_MAX) {
          queue.push({ seq: seq++, t: Date.now(), type: 'impressions', grid: ctx, items: list.slice(i, i + IMP_MAX).join(',') });
        }
      }
      impBuf = {};
    }

    function flush() {
      try { takeImpressions(); } catch (e) {}
      if (!queue.length) return;
      var body = JSON.stringify({
        sid: sid, cid: gaClientId(), pvid: pvid,
        page: { path: bare(location.pathname),
                type: cfg.template, lang: cfg.lang, theme: cfg.theme },
        vw: window.innerWidth, vh: window.innerHeight,
        ph: document.documentElement.scrollHeight,
        x: experiments() || undefined,
        events: queue.splice(0, queue.length)
      });
      var sent = false;
      try { sent = navigator.sendBeacon(cfg.endpoint, new Blob([body], { type: 'text/plain' })); } catch (e) {}
      if (!sent) { try { fetch(cfg.endpoint, { method: 'POST', body: body, keepalive: true, mode: 'no-cors' }); } catch (e) {} }
    }

    var refHost = null;
    try { refHost = document.referrer && new URL(document.referrer).host !== location.host ? new URL(document.referrer).hostname : null; } catch (e) {}
    push('page_view', { placement: ch, target: refHost });
    expViews().forEach(function (a) { push('exp_view', { target: a.key, placement: a.variant }); });

    // theme events (v12) kept before consent and start, then every later one
    var earlyUi = uiSink;
    uiSink = function (e) { push('exp_view', e); };
    earlyUi.forEach(uiSink);

    // errors caught before consent and start, then every later one
    var early = errorSink;
    errorSink = function (e) { push('js_error', e); };
    early.forEach(errorSink);

    // --- Speed: Core Web Vitals of this page view, sent once when it's left ---------------------
    // vital: target = LCP | INP | CLS | FCP | TTFB, ms = value (CLS x 1000), zone = zone of the LCP element.
    var vitals = { cls: 0, clsWin: 0, clsLast: 0, clsFirst: 0, inp: 0, lcp: null, lcpZone: null };
    function perf(type, fn, opts) {
      try { new PerformanceObserver(function (l) { l.getEntries().forEach(fn); }).observe(Object.assign({ type: type, buffered: true }, opts || {})); } catch (e) {}
    }
    // v14: when the page was first hidden (0 = loaded in the background); a paint after it waited for the page to be shown.
    // Browsers without visibility-state entries (Safari, Firefox) know only what this script saw since it started.
    var hiddenAt = document.visibilityState === 'hidden' && !document.prerendering ? 0 : Infinity;
    function activation() {
      var nav = performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
      return (nav && nav.activationStart) || 0;
    }
    function firstHidden() {
      try {
        var act = activation();
        var h = performance.getEntriesByType('visibility-state').filter(function (e) { return e.name === 'hidden' && e.startTime >= act; })[0];
        if (h) return Math.min(h.startTime, hiddenAt);
      } catch (e) {}
      return hiddenAt;
    }
    perf('largest-contentful-paint', function (en) {
      if (en.startTime >= firstHidden()) return;
      vitals.lcp = en.startTime;
      var z = en.element && zoneOf(en.element);
      vitals.lcpZone = z ? z.zone : null;
    });
    perf('layout-shift', function (en) {  // CLS: the worst 5 s window of shifts less than 1 s apart
      if (en.hadRecentInput) return;
      if (vitals.clsWin && en.startTime - vitals.clsLast < 1000 && en.startTime - vitals.clsFirst < 5000) vitals.clsWin += en.value;
      else { vitals.clsWin = en.value; vitals.clsFirst = en.startTime; }
      vitals.clsLast = en.startTime;
      if (vitals.clsWin > vitals.cls) vitals.cls = vitals.clsWin;
    });
    perf('event', function (en) {  // INP: the slowest interaction (fine for the few per page view)
      if (en.interactionId && en.duration > vitals.inp) vitals.inp = en.duration;
    }, { durationThreshold: 40 });
    var vitalsSent = false;
    function sendVitals() {
      if (vitalsSent) return;
      vitalsSent = true;
      try {
        var nav = performance.getEntriesByType('navigation')[0];
        var fcp = performance.getEntriesByName('first-contentful-paint')[0];
        var act = activation();  // v14: a prerendered page counts from when it was shown
        if (nav && nav.responseStart > 0) push('vital', { target: 'TTFB', ms: Math.max(nav.responseStart - act, 0) });
        if (fcp && fcp.startTime < firstHidden()) push('vital', { target: 'FCP', ms: Math.max(fcp.startTime - act, 0) });
        if (vitals.lcp != null) push('vital', { target: 'LCP', ms: Math.max(vitals.lcp - act, 0), zone: vitals.lcpZone });
        push('vital', { target: 'CLS', ms: vitals.cls * 1000 });
        if (vitals.inp) push('vital', { target: 'INP', ms: vitals.inp });
      } catch (e) {}
    }

    // --- Clarity: tag its recordings with the Squary session, so Squary can link to them -------
    (function tagClarity(n) {
      if (typeof window.clarity === 'function') { try { window.clarity('set', 'sq_sid', sid); } catch (e) {} }
      else if (n < 10) setTimeout(function () { tagClarity(n + 1); }, 2000);
    })(0);

    // --- Shop events: real add to cart, checkout start, session id on the cart ---------------
    var origFetch = window.fetch && window.fetch.bind(window);
    var cartTagged = false;
    try { cartTagged = sessionStorage.getItem('_sq_cart') === sid; } catch (e) {}
    function tagCart() {
      if (cartTagged || !origFetch) return;
      cartTagged = true;
      origFetch('/cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: { _sq_sid: sid } }) })
        .then(function (r) { if (r.ok) { try { sessionStorage.setItem('_sq_cart', sid); } catch (e) {} } else cartTagged = false; })
        .catch(function () { cartTagged = false; });
    }
    function added(json) {
      var items = json && (json.items || (json.product_id ? [json] : []));
      (items || []).forEach(function (it) {
        push('add_to_cart', { target: String(it.product_id || ''), placement: String(it.variant_id || it.id || ''),
          ms: it.quantity || 1, component: 'cart:add' });
      });
      if (items && items.length) { tagCart(); flush(); }
    }
    var CART_ADD = /\/cart\/add(\.js)?(\?|$)/;
    // A failed cart call (sold out, too many, server error) is a js_error of kind cart_api; the
    // message is Shopify's own description, e.g. "You can't add more X to the cart."
    var CART_API = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
    function cartFailed(url, status, json) {
      var path = String(url).replace(/^https?:\/\/[^/]+/, '').split('?')[0];
      var why = json && (json.description || json.message);
      reportError('cart_api', path + ' ' + (status || 'network error') + (why ? ': ' + why : ''), null, null, status);
    }
    if (origFetch) {
      window.fetch = function (input) {
        var url = typeof input === 'string' ? input : (input && input.url) || '';
        var p = origFetch.apply(window, arguments);
        if (CART_ADD.test(url)) {
          p.then(function (r) { if (r.ok) r.clone().json().then(added).catch(function () {}); }).catch(function () {});
        }
        if (CART_API.test(url)) {
          p.then(function (r) {
            if (r.ok) return;
            r.clone().json().then(function (j) { cartFailed(url, r.status, j); }).catch(function () { cartFailed(url, r.status); });
          }).catch(function () { cartFailed(url, 0); });
        }
        return p;
      };
    }
    var xo = XMLHttpRequest.prototype.open, xs = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function (m, url) { this._sqUrl = url; return xo.apply(this, arguments); };
    XMLHttpRequest.prototype.send = function () {
      var x = this;
      if (CART_ADD.test(String(x._sqUrl || ''))) {
        x.addEventListener('load', function () { if (x.status >= 200 && x.status < 300) { try { added(JSON.parse(x.responseText)); } catch (e) {} } });
      }
      if (CART_API.test(String(x._sqUrl || ''))) {
        x.addEventListener('loadend', function () {
          if (x.status >= 200 && x.status < 300) return;
          var j = null;
          try { j = JSON.parse(x.responseText); } catch (e) {}
          cartFailed(x._sqUrl, x.status, j);
        });
      }
      return xs.apply(this, arguments);
    };
    // A real form post to /cart/add (no AJAX): only when the theme didn't take over the submit,
    // which it does for AJAX adds (those are counted from the /cart/add request above).
    document.addEventListener('submit', function (ev) {
      var form = ev.target;
      var action = (form && form.getAttribute && form.getAttribute('action')) || '';
      setTimeout(function () {
        if (ev.defaultPrevented) return;
        if (/\/cart\/add/.test(action)) {
          var v = form.querySelector('[name="id"]');
          push('add_to_cart', { placement: v ? String(v.value) : '', ms: 1, component: 'cart:add_form' });
          tagCart();
          flush();
        } else if (/\/checkout/.test(action)) { push('begin_checkout', { component: 'cart:checkout_form' }); flush(); }
      }, 0);
    });
    document.addEventListener('click', function (ev) {
      if (!ev.target || !ev.target.closest) return;
      var el = ev.target.closest('[data-checkout-button], button[name="checkout"]');
      // v12: a link to the checkout by where it really goes: the cart page's 'Go to checkout' is href="checkout" (relative),
      // which a[href*="/checkout"] missed (only ~40% of its clicks counted as a checkout start)
      if (!el) {
        var a = ev.target.closest('a[href]');
        try { if (a && /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?checkouts?(?:\/|$)/i.test(new URL(a.href, location.href).pathname)) el = a; } catch (e) {}
      }
      if (el) { push('begin_checkout', { component: componentOf(el, null) || 'checkout' }); flush(); }
    }, true);

    // --- Session replay -----------------------------------------------------------------------
    // Recorded: REPLAY_RATE of sessions (by session id), plus every session from its first add to cart on
    // (v8): the cart-to-checkout part of almost every order gets a recording. The flag lives for the tab
    // session (sessionStorage), so the next pages are recorded too.
    // v11: on the pages of BUFFER_PAGES, the other sessions record the page view into memory ('buffer' mode):
    // nothing leaves the browser unless an add to cart happens on that page; then the buffer goes out as the
    // replay of this page view (same chunks, seq 0, 1, ...) and recording carries on as in v8 ('live' mode).
    // Leaving the page without an add to cart drops the buffer. Earlier pages of the session are never kept.
    var forced = false, replayMode = null;  // null | 'buffer' | 'live'
    try { if (/[?&]sq_replay=1/.test(location.search)) sessionStorage.setItem('_sq_replay', '1'); forced = sessionStorage.getItem('_sq_replay') === '1'; } catch (e) {}
    var goLive = null;  // set once rrweb records in buffer mode: sends the buffer, then records live
    var startReplay = function (live) {
      if (replayMode === 'live' || NO_REPLAY.test(location.pathname.replace(/^\/[a-z]{2}(?=\/)/, ''))) return;
      if (replayMode === 'buffer') {
        if (live) { replayMode = 'live'; if (goLive) goLive(); }  // before rrweb is loaded: onload starts live
        return;
      }
      replayMode = live ? 'live' : 'buffer';
      idle(function () {
        var tag = document.createElement('script');
        tag.src = BASE + '/rrweb.js';
        tag.async = true;
        tag.onload = function () {
          if (!window.rrwebRecord || !replayMode) return;
          var rbuf = [], rseq = 0, rsize = 0;
          // Chunks are gzipped in the browser (CompressionStream) where it can: ~10x smaller.
          var post = function (body, unloading) {
            var go = function (data, size) {
              // keepalive requests are capped at 64 KB; bigger chunks go as a normal request
              try { origFetch(BASE + '/r', { method: 'POST', body: data, keepalive: unloading && size < 60000, mode: 'no-cors' }); } catch (e) {}
            };
            if (window.CompressionStream && !unloading) {
              new Response(new Blob([body]).stream().pipeThrough(new CompressionStream('gzip'))).blob()
                .then(function (z) { go(z, z.size); }).catch(function () { go(body, body.length); });
            } else go(body, body.length);
          };
          var send = function (unloading) {
            while (rbuf.length) {  // at most 1500 events per chunk (a sent buffer can hold more)
              var body = JSON.stringify({ sid: sid, pvid: pvid, seq: rseq++, vw: window.innerWidth, vh: window.innerHeight,
                page: { path: bare(location.pathname), type: cfg.template, lang: cfg.lang, theme: cfg.theme },
                events: rbuf.splice(0, 1500) });
              post(body, unloading);
            }
            rsize = 0;
          };
          // v11 buffer: segments, each starting with a full snapshot (Meta + FullSnapshot), at most two kept. When
          // the newest one reaches half the cap (BUFFER_MS or BUFFER_BYTES of incremental events), rrweb takes a
          // fresh full snapshot (a checkout) that starts the next segment and the oldest is dropped: the buffer
          // always replays from a snapshot and holds between half and all of the cap. A page view shorter than
          // half the cap (most of them) never takes a second snapshot.
          var segs = [{ t: Date.now(), bytes: 0, events: [] }], checkoutDue = false;
          var buffer = function (e, isCheckout) {
            if (isCheckout && e.type === 4) {  // Meta of a checkout: a new segment
              segs.push({ t: e.timestamp || Date.now(), bytes: 0, events: [] });
              if (segs.length > 2) segs.shift();
              checkoutDue = false;
            }
            var s = segs[segs.length - 1];
            s.events.push(e);
            if (e.type !== 3) return;
            // size: mutations measured (they carry the DOM that changed), other incremental events ~100 bytes
            if (e.data && e.data.source === 0) { try { s.bytes += JSON.stringify(e.data).length; } catch (x) { s.bytes += 1000; } }
            else s.bytes += 100;
            if (!checkoutDue && (s.bytes >= BUFFER_BYTES / 2 || (e.timestamp || Date.now()) - s.t >= BUFFER_MS / 2)) {
              checkoutDue = true;  // not inside rrweb's own emit
              setTimeout(function () { try { window.rrwebRecord.takeFullSnapshot(true); } catch (x) { checkoutDue = false; } }, 0);
            }
          };
          var dropBuffer = function () { segs = []; };
          // A tab left open records only until 30 min without input (or 1 h on one page):
          // carousels and timers would otherwise keep a background tab recording for days.
          var lastInput = Date.now(), started = Date.now();
          ['pointerdown', 'pointermove', 'keydown', 'scroll', 'touchstart'].forEach(function (t) {
            window.addEventListener(t, function () { lastInput = Date.now(); }, { passive: true, capture: true });
          });
          var stopRecording = window.rrwebRecord({
            emit: function (e, isCheckout) {
              if (replayMode === 'buffer') return buffer(e, isCheckout);
              rbuf.push(e); rsize += 1; if (rsize >= 1500) send(false);
            },
            maskAllInputs: true,
            maskTextClass: 'sq-mask',
            blockClass: 'sq-block',
            inlineStylesheet: false,
            collectFonts: false,
            sampling: { mousemove: 100, scroll: 150, media: 800, input: 'last' },
          });
          var stop = function () {
            clearInterval(timer);
            if (stopRecording) { try { stopRecording(); } catch (x) {} stopRecording = null; }
          };
          goLive = function () {
            goLive = null;
            if (!segs.length) return;
            // the page so far, from the oldest kept full snapshot; each segment in its own chunks, so no chunk carries
            // two snapshots (a long page's snapshot is ~2 MB before gzip; the collector takes up to 8 MB)
            var kept = segs;
            segs = [];
            kept.forEach(function (s) { rbuf = s.events; send(false); });
            rbuf = [];
            lastSend = Date.now();
          };
          if (replayMode === 'live') goLive = null;
          // pieces every 20 s instead of 5 s: a quarter of the writes (the storage cost is mostly writes)
          var lastSend = Date.now();
          var timer = setInterval(function () {
            var now = Date.now();
            if (replayMode === 'live' && now - lastSend >= 20000) { send(false); lastSend = now; }
            if (now - lastInput > 30 * 60000 || now - started > 60 * 60000) {
              stop();
              if (replayMode === 'buffer') { dropBuffer(); goLive = null; replayMode = null; }  // an add to cart later starts afresh (v8)
            }
          }, 5000);
          window.addEventListener('pagehide', function () {
            if (replayMode === 'live') { clearInterval(timer); send(true); return; }
            // buffer mode: no add to cart on this page, nothing is sent; stop, so a page back from the bfcache
            // never sends events without their snapshot (an add to cart there starts a new recording)
            stop(); dropBuffer(); goLive = null;
            if (replayMode === 'buffer') replayMode = null;
          });
          document.addEventListener('visibilitychange', function () { if (document.visibilityState === 'hidden' && replayMode === 'live') send(true); });
        };
        document.head.appendChild(tag);
      });
    };
    window.__sqReplayFromCart = function () {
      try { sessionStorage.setItem('_sq_replay', '1'); } catch (e) {}
      startReplay(true);
    };
    if (forced || (REPLAY_RATE > 0 && bucketOf(sid) < REPLAY_RATE * 100)) startReplay(true);
    else if (BUFFER_ON) startReplay(false);

    // Zone exposure: at least half visible for 1 s; duration summed until the page is left.
    var seen = new Map(); // zone el -> {since, ms, sent}
    var io = new IntersectionObserver(function (entries) {
      var now = Date.now();
      entries.forEach(function (en) {
        var s = seen.get(en.target) || { since: null, ms: 0, sent: false };
        // seen = at least half of it visible, or (tall sections) it fills at least half the screen's height, across
        // at least half its width (closed drawers and overlays have no height: never seen; their clicks still count).
        // v12: the width check. A slide just off screen (the hero's 2nd banner, a carousel's next card) touches the
        // screen's edge: the browser reports it as intersecting with 0 px width and its full height, and it counted
        // as seen on every page view.
        var r = en.intersectionRect, b = en.boundingClientRect;
        var shown = en.isIntersecting && r.height > 0 && r.width > 0 &&
          (en.intersectionRatio >= 0.5 || (r.height >= window.innerHeight / 2 && r.width >= Math.min(b.width, window.innerWidth) / 2));
        if (shown) s.since = s.since || now;
        else if (s.since) { s.ms += now - s.since; s.since = null; }
        seen.set(en.target, s);
      });
    }, { threshold: [0, 0.1, 0.25, 0.5, 0.75, 1] });
    function observe(root) {
      var sel = ZONE + ',' + SECTION + ',' + APPS.map(function (a) { return a[0]; }).join(',');
      var list = [].slice.call((root.querySelectorAll ? root : document).querySelectorAll(sel));
      if (root.matches && root.matches(sel)) list.push(root);
      list.forEach(function (z) {
        if (!seen.has(z) && zoneOf(z)) { seen.set(z, { since: null, ms: 0, sent: false }); io.observe(z); }
      });
    }
    observe(document);

    // v9: product-card impressions: at least half of the card visible for 1 s without interruption.
    // Recorded once per page view and grid context (card index, product id, page, sort, filters):
    // the same product shown again after a filter or sort change is a new impression.
    var cardTimers = typeof WeakMap === 'function' ? new WeakMap() : null;
    var cardsSeen = cardTimers && new WeakSet();
    function cardShown(card) {
      if (!card.isConnected || document.visibilityState !== 'visible') return;
      var g = gridOf(card);
      if (!g) return;
      var key = g.ctx + '#' + g.idx + ':' + g.pid;
      if (impSeen[key]) return;
      impSeen[key] = true;
      (impBuf[g.ctx] = impBuf[g.ctx] || []).push(g.idx + ':' + g.pid);
      if (cardIo) cardIo.unobserve(card);  // a re-rendered grid (filter, sort, AI answer) brings new card elements
    }
    var cardIo = GRID_PAGE && cardTimers && window.IntersectionObserver ? new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        var t = cardTimers.get(en.target);
        if (en.isIntersecting && en.intersectionRatio >= 0.5) {
          if (!t) cardTimers.set(en.target, setTimeout(function () { cardTimers.delete(en.target); cardShown(en.target); }, VIEW_MS));
        } else if (t) { clearTimeout(t); cardTimers.delete(en.target); }
      });
    }, { threshold: [0, 0.5] }) : null;
    function observeCards() {
      if (!cardIo) return;
      [].forEach.call(document.querySelectorAll(GRID), function (grid) {
        [].forEach.call(grid.querySelectorAll(CARD), function (c) {
          if (!cardsSeen.has(c)) { cardsSeen.add(c); cardIo.observe(c); }
        });
      });
    }
    try { observeCards(); } catch (e) {}

    // Sections rendered later (lazy sections, AJAX) get observed too.
    // One rescan per idle moment, however many nodes the theme adds.
    var rescan = null;
    new MutationObserver(function () {
      if (!rescan) rescan = idle(function () { rescan = null; observe(document); try { observeCards(); } catch (e) {} });
    }).observe(document.body, { childList: true, subtree: true });

    function zoneViews() {
      var now = Date.now();
      seen.forEach(function (s, el) {
        var ms = s.ms + (s.since ? now - s.since : 0);
        if (ms >= VIEW_MS && !s.sent) {
          var z = zoneOf(el);
          if (z) push('zone_view', { zone: z.zone, placement: z.placement, ms: ms });
          s.sent = true;
        }
      });
    }

    // Hover: desktop only.
    if (window.matchMedia && matchMedia('(hover: hover) and (pointer: fine)').matches) {
      var hoverZone = null, hoverSince = 0;
      document.addEventListener('mouseover', function (ev) {
        var z = zoneOf(ev.target);
        var el = z ? z.el : null;
        if (el === (hoverZone && hoverZone.el)) return;
        if (hoverZone && Date.now() - hoverSince >= HOVER_MS) {
          push('hover', { zone: hoverZone.zone, placement: hoverZone.placement, ms: Date.now() - hoverSince });
        }
        hoverZone = z; hoverSince = Date.now();
      }, { passive: true });
    }

    // Clicks, rage clicks (3 within 700 ms and 30 px) and dead clicks (on nothing interactive).
    var recent = [], lastLabel = null;
    document.addEventListener('click', function (ev) {
      if (ev.isTrusted === false) return;  // clicks made by scripts (e.g. the AI filter picking a card's swatch) are not the shopper's
      var el = ev.target;
      if (!el || el.nodeType !== 1) return;
      // v10: a click on a label makes the browser click its input too (a second, trusted event): count the shopper's one
      if (el.tagName === 'INPUT' && lastLabel && Date.now() - lastLabel.t < 100 &&
          (lastLabel.el.contains(el) || (el.id && lastLabel.el.htmlFor === el.id))) { lastLabel = null; return; }
      var lab = el.closest && el.closest('label');
      lastLabel = lab ? { el: lab, t: Date.now() } : null;
      var z = zoneOf(el);
      var e = { target: targetOf(el), component: componentOf(el, z && z.el) };
      if (z) {
        var r = z.el.getBoundingClientRect();
        e.zone = z.zone; e.placement = z.placement;
        // v12: a click proves the zone was seen: one not yet on screen for 1 s (a swipe and a quick tap) counts as seen now
        var vs = seen.get(z.el);
        if (vs && !vs.sent) {
          vs.sent = true;
          push('zone_view', { zone: z.zone, placement: z.placement, ms: Math.max(VIEW_MS, vs.ms + (vs.since ? Date.now() - vs.since : 0)) });
        }
        e.x = r.width ? (ev.clientX - r.left) / r.width : null;
        e.y = r.height ? (ev.clientY - r.top) / r.height : null;
        // the zone's size, so the heatmap can place a click at its pixel distance from the zone's
        // top (or bottom): zones like product-intro grow with the screen width, fractions drift
        e.zw = Math.round(r.width); e.zh = Math.round(r.height);
        // v6: the position inside the clicked component (data-sq-c / data-component), so the heatmap can
        // put the click on that component wherever it sits on another page of the same type
        var comp = el.closest('[data-sq-c],[data-component]');
        if (comp && comp !== document.body && z.el.contains(comp)) {
          var cr = comp.getBoundingClientRect();
          if (cr.width && cr.height) { e.cx = (ev.clientX - cr.left) / cr.width; e.cy = (ev.clientY - cr.top) / cr.height; }
        }
      } else {
        e.x = ev.pageX / document.documentElement.scrollWidth;
        e.y = ev.pageY / document.documentElement.scrollHeight;
      }
      // v9: a click inside a product card of the grid carries the card's place in it
      if (GRID_PAGE) {
        try {
          var g = gridOf(el.closest(CARD));
          if (g) { e.grid = g.ctx; e.items = g.idx + ':' + g.pid; }
        } catch (x) {}
      }
      // v10: the clicked element among its likes, and the spot inside it
      try {
        var k = elKey(el, z, ev);
        if (k) { e.ek = k.key; e.ex = k.x; e.ey = k.y; }
      } catch (x) {}
      push('click', e);

      var now = Date.now();
      recent = recent.filter(function (c) { return now - c.t < 700 && Math.abs(c.x - ev.clientX) < 30 && Math.abs(c.y - ev.clientY) < 30; });
      recent.push({ t: now, x: ev.clientX, y: ev.clientY });
      // rage and dead clicks carry the click's position too, so the heatmap can draw them
      var at = { zone: e.zone, placement: e.placement, target: e.target, component: e.component, x: e.x, y: e.y, zw: e.zw, zh: e.zh, cx: e.cx, cy: e.cy,
                 ek: e.ek, ex: e.ex, ey: e.ey };
      if (recent.length === 3) push('rage_click', at);
      if (!interactive(el)) push('dead_click', at);
    }, { capture: true, passive: true });

    // Scroll depth: the deepest point reached, sent with page_exit.
    function scrolled() {
      var h = document.documentElement.scrollHeight;
      var pct = h ? Math.min(100, Math.round((window.scrollY + window.innerHeight) / h * 100)) : 0;
      if (pct > maxScroll) maxScroll = pct;
    }
    scrolled();
    window.addEventListener('scroll', scrolled, { passive: true });

    // Leaving: count zone views, then send everything. pagehide covers bfcache and mobile.
    var exited = false;
    function hidden() {
      if (hiddenAt === Infinity) hiddenAt = performance.now();
      if (visibleSince) { visibleMs += Date.now() - visibleSince; visibleSince = null; }
      try { zoneViews(); } catch (e) {}  // never lose the batch over a zone
      sendVitals();  // values so far: mobile browsers often never fire pagehide
      flush();
    }
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'hidden') hidden();
      else { visibleSince = Date.now(); sessionId(); }
    });
    window.addEventListener('pagehide', function () {
      if (exited) return;
      exited = true;
      if (visibleSince) { visibleMs += Date.now() - visibleSince; visibleSince = null; }
      try { zoneViews(); } catch (e) {}
      sendVitals();
      push('page_exit', { pct: maxScroll, ms: visibleMs });
      flush();
    });
  }

  // Start when the browser is idle, so tracking never competes with the page's first paint.
  if (consented()) idle(start);
  else document.addEventListener('visitorConsentCollected', function (ev) {
    if (ev.detail && ev.detail.analyticsAllowed && !window.__squaryStarted) { window.__squaryStarted = true; idle(start); }
  });
})();
